// src/pages/Admin/PainelComprovativos.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, CircularProgress,
  Alert, Tooltip, IconButton, Badge
} from '@mui/material';
import {
  CheckCircle as ConfirmIcon,
  Cancel as RejectIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  AccessTime as PendingIcon
} from '@mui/icons-material';
import {
  listarPedidosPendentes,
  listarTodosPedidos,
  confirmarPedido,
  rejeitarPedido
} from '../../services/billingService';

const METODO_LABEL = {
  MPESA: 'M-Pesa', EMOLA: 'E-mola',
  TRANSFERENCIA_BANCARIA: 'Transferência', BIM: 'BIM',
  BCI: 'BCI', MOZABANK: 'MozaBanco', STANDARDBANK: 'Standard Bank'
};

const STATUS_COLOR = {
  PENDENTE: 'warning', CONFIRMADO: 'success',
  REJEITADO: 'error',  CANCELADO: 'default'
};

export default function PainelComprovativos() {
  const [pedidos, setPedidos]         = useState([]);
  const [loading, setLoading]         = useState(false);
  const [erro, setErro]               = useState('');
  const [sucesso, setSucesso]         = useState('');
  const [filtroStatus, setFiltroStatus] = useState('PENDENTE');

  // Dialog de confirmação
  const [dialogo, setDialogo]         = useState({ aberto: false, tipo: '', pedido: null });
  const [textoDialog, setTextoDialog] = useState('');
  const [processando, setProcessando] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const res = filtroStatus === 'PENDENTE'
        ? await listarPedidosPendentes()
        : await listarTodosPedidos({ status: filtroStatus || undefined, limit: 100 });
      setPedidos(res.pedidos ?? []);
    } catch (e) {
      setErro(e.response?.data?.erro || 'Erro ao carregar pedidos');
    } finally {
      setLoading(false);
    }
  }, [filtroStatus]);

  useEffect(() => { carregar(); }, [carregar]);

  const abrirDialogo = (tipo, pedido) => {
    setTextoDialog('');
    setDialogo({ aberto: true, tipo, pedido });
  };

  const fecharDialogo = () => setDialogo({ aberto: false, tipo: '', pedido: null });

  const executarAcao = async () => {
    setProcessando(true);
    try {
      if (dialogo.tipo === 'confirmar') {
        await confirmarPedido(dialogo.pedido.id, textoDialog);
        setSucesso(`Módulo "${dialogo.pedido.modulo_nome}" activado para ${dialogo.pedido.instituicao_nome}`);
      } else {
        await rejeitarPedido(dialogo.pedido.id, textoDialog);
        setSucesso('Pedido rejeitado.');
      }
      fecharDialogo();
      carregar();
    } catch (e) {
      setErro(e.response?.data?.erro || 'Erro ao processar acção');
    } finally {
      setProcessando(false);
    }
  };

  const pendentesCount = pedidos.filter(p => p.status === 'PENDENTE').length;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Cabeçalho */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Badge badgeContent={filtroStatus === 'PENDENTE' ? pendentesCount : 0} color="warning">
            <PendingIcon color="primary" sx={{ fontSize: 32 }} />
          </Badge>
          <Box>
            <Typography variant="h5" fontWeight={700}>Comprovativos de Pagamento</Typography>
            <Typography variant="body2" color="text.secondary">
              Confirme ou rejeite pedidos de activação de módulos
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          {['PENDENTE', 'CONFIRMADO', 'REJEITADO', ''].map(s => (
            <Button
              key={s}
              size="small"
              variant={filtroStatus === s ? 'contained' : 'outlined'}
              color={s === 'PENDENTE' ? 'warning' : s === 'CONFIRMADO' ? 'success' : s === 'REJEITADO' ? 'error' : 'inherit'}
              onClick={() => setFiltroStatus(s)}
            >
              {s || 'Todos'}
            </Button>
          ))}
          <Tooltip title="Actualizar">
            <IconButton onClick={carregar}><RefreshIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      {erro    && <Alert severity="error"   onClose={() => setErro('')}    sx={{ mb: 2 }}>{erro}</Alert>}
      {sucesso && <Alert severity="success" onClose={() => setSucesso('')} sx={{ mb: 2 }}>{sucesso}</Alert>}

      <Paper elevation={2}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={6}><CircularProgress /></Box>
        ) : pedidos.length === 0 ? (
          <Box p={6} textAlign="center">
            <Typography color="text.secondary">
              {filtroStatus === 'PENDENTE' ? 'Nenhum comprovativo pendente.' : 'Nenhum pedido encontrado.'}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell>#</TableCell>
                  <TableCell>Instituição</TableCell>
                  <TableCell>Módulo</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell align="right">Valor (MZN)</TableCell>
                  <TableCell>Método</TableCell>
                  <TableCell>Referência</TableCell>
                  <TableCell>Data Pedido</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="center">Acções</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pedidos.map(p => (
                  <TableRow key={p.id} hover>
                    <TableCell>{p.id}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{p.instituicao_nome}</Typography>
                      <Typography variant="caption" color="text.secondary">{p.instituicao_email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{p.modulo_nome}</Typography>
                      <Typography variant="caption" color="text.secondary">{p.modulo_codigo}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={p.tipo_cobranca} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={600}>
                        {(p.valor_mzn / 100).toLocaleString('pt-MZ')}
                      </Typography>
                    </TableCell>
                    <TableCell>{METODO_LABEL[p.metodo_pagamento] || p.metodo_pagamento}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {p.referencia_pagamento || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {new Date(p.created_at).toLocaleDateString('pt-PT')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={p.status}
                        color={STATUS_COLOR[p.status] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {p.status === 'PENDENTE' && (
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <Tooltip title="Confirmar pagamento">
                            <IconButton
                              color="success" size="small"
                              onClick={() => abrirDialogo('confirmar', p)}
                            >
                              <ConfirmIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Rejeitar">
                            <IconButton
                              color="error" size="small"
                              onClick={() => abrirDialogo('rejeitar', p)}
                            >
                              <RejectIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                      {p.observacoes && (
                        <Tooltip title={p.observacoes}>
                          <IconButton size="small"><ViewIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Dialog de confirmação / rejeição */}
      <Dialog open={dialogo.aberto} onClose={fecharDialogo} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogo.tipo === 'confirmar' ? '✅ Confirmar Pagamento' : '❌ Rejeitar Pedido'}
        </DialogTitle>
        <DialogContent>
          {dialogo.pedido && (
            <Box mb={2}>
              <Typography variant="body2"><strong>Instituição:</strong> {dialogo.pedido.instituicao_nome}</Typography>
              <Typography variant="body2"><strong>Módulo:</strong> {dialogo.pedido.modulo_nome}</Typography>
              <Typography variant="body2">
                <strong>Valor:</strong> {dialogo.pedido.valor_mzn ? (dialogo.pedido.valor_mzn / 100).toLocaleString('pt-MZ') : '—'} MZN
              </Typography>
              <Typography variant="body2"><strong>Referência:</strong> {dialogo.pedido.referencia_pagamento || '—'}</Typography>
            </Box>
          )}
          <TextField
            fullWidth multiline rows={3}
            label={dialogo.tipo === 'confirmar' ? 'Observações (opcional)' : 'Motivo da rejeição (opcional)'}
            value={textoDialog}
            onChange={e => setTextoDialog(e.target.value)}
            variant="outlined"
            sx={{ mt: 1 }}
          />
          {dialogo.tipo === 'confirmar' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Ao confirmar, o módulo será activado imediatamente e será enviado email à instituição.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharDialogo} disabled={processando}>Cancelar</Button>
          <Button
            onClick={executarAcao}
            variant="contained"
            color={dialogo.tipo === 'confirmar' ? 'success' : 'error'}
            disabled={processando}
            startIcon={processando ? <CircularProgress size={16} /> : null}
          >
            {processando ? 'A processar...' : dialogo.tipo === 'confirmar' ? 'Confirmar e Activar' : 'Rejeitar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
